// Implements: F-UIX-015
/**
 * The one way to read interface text.
 *
 * `TranslationKey` is `keyof typeof es`, so `t("auth.login.noExiste")` is a
 * compile error and not an empty string somebody notices in production. The
 * parameters are typed the same way: `ParamsFor<K>` reads the placeholders out
 * of the VALUE of the key with a conditional type, so a key with `{seconds}`
 * cannot be rendered without `seconds`, and a key without placeholders takes
 * no second argument at all.
 *
 * There is no provider and no context here on purpose. The application ships
 * one language (AD-05); a provider would be a seam with nothing behind it. The
 * hook exists so every component already reads text through a hook the day a
 * second language needs one.
 */

import { es } from "./es";

export type TranslationKey = keyof typeof es;

/** The literal Spanish value of a key, which is what carries the placeholders. */
type TranslationValue<K extends TranslationKey> = (typeof es)[K];

/** Every `{name}` inside a template literal type, as a union of names. */
type Placeholder<S extends string> = S extends `${string}{${infer Name}}${infer Rest}`
  ? Name | Placeholder<Rest>
  : never;

/** What a value may be interpolated with. */
export type TranslationParams = Readonly<Record<string, string | number>>;

/**
 * `[]` for a key with no placeholder, `[params]` for a key with them.
 *
 * The `[X] extends [never]` shape is deliberate: a bare `X extends never`
 * distributes over the union and answers for each member instead of for the
 * union, which is how "has no placeholders" quietly becomes "has some".
 */
type ParamsFor<K extends TranslationKey> = [Placeholder<TranslationValue<K>>] extends [never]
  ? []
  : [params: Readonly<Record<Placeholder<TranslationValue<K>>, string | number>>];

const PLACEHOLDER_RE = /\{([^{}]+)\}/g;

/**
 * The Spanish text of `key`, with its placeholders filled in.
 *
 * @throws {Error} When a declared placeholder has no value. A half-rendered
 *   sentence with `{seconds}` still in it is worse than a loud failure.
 */
export function t<K extends TranslationKey>(key: K, ...params: ParamsFor<K>): string {
  const template: string = es[key];
  const values = params[0] as TranslationParams | undefined;

  return template.replace(PLACEHOLDER_RE, (_match, name: string) => {
    const value = values?.[name];
    if (value === undefined) {
      throw new Error(`Translation "${key}" needs the parameter "${name}"`);
    }
    return String(value);
  });
}

export interface Translation {
  readonly t: typeof t;
}

/** The translator, for components that should not import `t` directly. */
export function useTranslation(): Translation {
  return { t };
}
